import { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Loader2, FileText, CreditCard } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useBillingPolicy } from '../hooks/useBillingPolicy';

const API = process.env.REACT_APP_BACKEND_URL;

export default function InvoiceGenerationModal({ open, onClose, operation, onSuccess, mode = 'invoice' }) {
  const { toast } = useToast();
  const [policies, setPolicies] = useState([]);
  const [selectedPolicyId, setSelectedPolicyId] = useState('');
  const [customText, setCustomText] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [clientPolicySource, setClientPolicySource] = useState(null);
  const [includeTVA, setIncludeTVA] = useState(true);
  const billingPolicy = useBillingPolicy(open);
  const vatBlocked = !!billingPolicy?.vatBlocked;

  useEffect(() => {
    if (vatBlocked) setIncludeTVA(false);
  }, [vatBlocked]);

  useEffect(() => {
    if (open) {
      fetchPoliciesAndClientDefault();
      // Initialize TVA state from operation data
      const opHasTVA = operation?.applyTVA !== false && operation?.includeTVA !== false;
      setIncludeTVA(opHasTVA && !vatBlocked);
    }
  }, [open]);

  const fetchPoliciesAndClientDefault = async () => {
    setLoading(true);
    setClientPolicySource(null);
    try {
      const res = await axios.get(`${API}/api/payment-policies/`, {
      });
      setPolicies(res.data);

      // Try to find client's default payment policy
      const clientUserId = operation?.userId || operation?.client_id || operation?.createdById;
      let clientPolicy = null;
      if (clientUserId) {
        try {
          const userRes = await axios.get(`${API}/api/admin/users`, {
            params: { role: 'client', pageSize: 100 }
          });
          const users = userRes.data.users || userRes.data || [];
          const clientUser = users.find(u => u.id === clientUserId);
          if (clientUser?.default_payment_policy_id) {
            const exists = res.data.find(p => p.id === clientUser.default_payment_policy_id);
            if (exists) {
              clientPolicy = exists;
              setSelectedPolicyId(exists.id);
              setClientPolicySource('client');
            }
          }
        } catch (_) {}
      }

      // Fallback to system default
      if (!clientPolicy) {
        const def = res.data.find((p) => p.is_default);
        if (def) {
          setSelectedPolicyId(def.id);
          setClientPolicySource('default');
        }
      }
    } catch (e) {
      console.error('Error fetching policies:', e);
    } finally {
      setLoading(false);
    }
  };

  const selectedPolicy = policies.find((p) => p.id === selectedPolicyId);
  const isCustom = selectedPolicy?.code === 'CUSTOM';

  const handleGenerate = async () => {
    setGenerating(true);
    const opId = operation?.id;

    const bodyPayload = {};
    if (selectedPolicyId) bodyPayload.payment_policy_id = selectedPolicyId;
    if (isCustom && customText.trim()) bodyPayload.custom_payment_text = customText.trim();
    bodyPayload.include_tva = includeTVA;

    try {
      if (mode === 'invoice-from-quote') {
        const res = await axios.post(
          `${API}/api/documents/generate/invoice-from-quote/${opId}`,
          bodyPayload,
          {}
        );
        if (res.data.success) {
          toast({ title: 'Facture générée', description: `Facture ${res.data.invoice_number} créée et envoyée par email.` });
          try {
            const dl = await axios.get(`${API}/api/documents/download/${res.data.invoice_number}`, {
              responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([dl.data]));
            const a = document.createElement('a');
            a.href = url;
            a.download = `Facture_${res.data.invoice_number}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
          } catch (_) {}
          onSuccess?.();
          onClose();
        }
      } else {
        const res = await axios.post(
          `${API}/api/documents/generate/invoice/${opId}`,
          bodyPayload,
          { responseType: 'blob'}
        );
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `Facture_${operation?.operationNumber || opId?.substring(0, 8)}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        toast({ title: 'Facture générée', description: 'La facture a été téléchargée et envoyée par email.' });
        onSuccess?.();
        onClose();
      }
    } catch (error) {
      // Décoder l'erreur correctement (JSON ou blob)
      let msg = 'Impossible de générer la facture';
      try {
        const data = error.response?.data;
        if (data instanceof Blob) {
          const text = await data.text();
          const parsed = JSON.parse(text);
          if (typeof parsed?.detail === 'string') msg = parsed.detail;
        } else if (typeof data?.detail === 'string') {
          msg = data.detail;
        } else if (typeof data?.detail === 'object') {
          msg = JSON.stringify(data.detail);
        }
      } catch (_) {}
      toast({ title: 'Erreur', description: msg, variant: 'destructive' });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg" data-testid="invoice-generation-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base font-semibold">
            <FileText className="w-5 h-5" />
            Générer la Facture
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {/* Operation summary */}
            <div className="bg-gray-50 rounded-lg p-3 text-sm space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-500">Opération</span>
                <span className="font-medium">{operation?.operationNumber || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">Trajet</span>
                <span className="font-medium text-right max-w-[70%]">{(() => {
                  const shorten = (a) => {
                    if (!a) return '?';
                    const clean = a.replace(/,?\s*Côte d'Ivoire$/i, '');
                    const parts = clean.split(',').map(p => p.trim()).filter(Boolean);
                    const meaningful = parts.filter(p => p.toLowerCase() !== 'abidjan');
                    return meaningful[0] || parts[0] || a;
                  };
                  const stops = operation?.stops || operation?.etapes || [];
                  const pts = [shorten(operation?.departure), ...stops.map(s => shorten(s.address || s.name || '')), shorten(operation?.arrival)];
                  return pts.join(' → ');
                })()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500">Montant</span>
                <span className={`font-semibold ${(operation?.montantTotalTTC || operation?.finalPrice || operation?.estimatedPrice || 0) === 0 ? 'text-red-500' : 'text-gray-800'}`}>
                  {(operation?.montantTotalTTC || operation?.finalPrice || operation?.estimatedPrice || 0).toLocaleString('fr-FR')} F CFA
                </span>
              </div>
              {(operation?.montantTotalTTC || operation?.finalPrice || operation?.estimatedPrice || 0) === 0 && (
                <p className="text-xs text-amber-600 bg-amber-50 rounded px-2 py-1 mt-1">
                  ⚠ Le montant est à 0 — la facture sera générée mais pensez à modifier le prix de l'opération.
                </p>
              )}
            </div>

            {/* Payment policy selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1.5">
                <CreditCard className="w-4 h-4" />
                Modalités de paiement
                {clientPolicySource === 'client' && (
                  <span className="ml-1 text-[10px] font-semibold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded" data-testid="client-policy-badge">
                    Condition client
                  </span>
                )}
                {clientPolicySource === 'default' && (
                  <span className="ml-1 text-[10px] font-semibold bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">
                    Défaut système
                  </span>
                )}
              </label>
              <select
                data-testid="payment-policy-select"
                value={selectedPolicyId}
                onChange={(e) => {
                  setSelectedPolicyId(e.target.value);
                  setCustomText('');
                }}
                className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
              >
                <option value="">-- Choisir une modalité --</option>
                {policies.filter(p => p.is_active !== false).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>

              {policies.filter(p => p.is_active !== false).length === 0 && !loading && (
                <p className="mt-1 text-xs text-red-500">Aucune modalité disponible — vérifiez la configuration.</p>
              )}

              {selectedPolicy && !isCustom && (
                <p className="mt-2 text-xs text-gray-500 leading-relaxed bg-gray-50 rounded p-2">
                  {selectedPolicy.description || selectedPolicy.payment_text}
                </p>
              )}

              {selectedPolicy?.conditions?.length > 0 && (
                <ul className="mt-2 text-xs text-gray-600 space-y-1 pl-4 list-disc">
                  {selectedPolicy.conditions.map((c, i) => (
                    <li key={i}>{c.description}</li>
                  ))}
                </ul>
              )}
            </div>

            {/* TVA Toggle — grisé si le régime d'imposition interdit la TVA */}
            <div
              className={`flex items-center justify-between py-3 px-3 rounded-lg ${vatBlocked ? 'bg-gray-100 border border-gray-200' : 'bg-gray-50'}`}
              data-testid="tva-toggle-section"
            >
              <div>
                <p className={`text-sm font-medium ${vatBlocked ? 'text-gray-500' : 'text-gray-700'}`}>
                  Appliquer la TVA (18 %)
                  {vatBlocked && (
                    <span className="ml-2 text-xs font-normal uppercase tracking-wide text-gray-500">
                      indisponible
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500 max-w-md">
                  {vatBlocked
                    ? billingPolicy.message
                    : includeTVA
                      ? 'La TVA sera incluse dans la facture'
                      : 'Facture sans TVA (exonérée)'}
                </p>
              </div>
              <Switch
                checked={includeTVA && !vatBlocked}
                disabled={vatBlocked}
                onCheckedChange={setIncludeTVA}
                data-testid="tva-toggle"
              />
            </div>

            {/* Custom text for negotiated terms */}
            {isCustom && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Conditions négociées <span className="text-red-500">*</span>
                </label>
                <textarea
                  data-testid="custom-payment-text"
                  value={customText}
                  onChange={(e) => setCustomText(e.target.value)}
                  rows={3}
                  placeholder="Ex: 60% avant mise à disposition, 40% à 15 jours après livraison..."
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 resize-none"
                />
                <p className="mt-1 text-xs text-gray-400">
                  Ce texte apparaîtra sur la facture et dans l'email envoyé au client.
                </p>
              </div>
            )}
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={generating} data-testid="cancel-invoice-btn">
            Annuler
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={generating || !selectedPolicyId}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="confirm-generate-invoice-btn"
          >
            {generating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Génération...
              </>
            ) : (
              'Générer et envoyer'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
